// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { computeCostSplit } from "@/lib/budget-gauge"

// Snapshot-payload shape for this panel — the four nested Spend fields from
// CycleSnapshotDto (PRD §M5/§M11; mirrored in src/types/event-journal.d.ts).
// Replaces the old flat total_cost / total_*_cost_by_iter fields (INV-8,
// breaking — the wire no longer carries a per-iteration cost series, only the
// cycle-wide ledger-to-date split by category).
// INV-CYCLE-ID-SCOPE: CycleSnapshotDto deliberately omits cycle_id (cycle
// scope lives on the outer event envelope); this subset reflects that.
export interface CostChartsSnapshotPayload {
  spend: Spend
  architect_spend: Spend
  genetic_spend: Spend
  evaluation_spend: Spend
  // Optional frozen reference set — carries the references' `cost_per_run`
  // for the cost-tab level line. Absent ⇒ no line, cost tab renders
  // exactly today's dashboard.
  reference_set?: ReferenceSet
}

// One row of the cost-by-category breakdown — Architect / Genetic /
// Evaluation (closes deviations 18 & 19: genetic and evaluation now carry a
// real split same as architect, PRD §M4).
export interface CostByCategoryRow {
  category: "Architect" | "Genetic" | "Evaluation"
  total: number
  // null when this category's Spend.split_available is false — never a fabricated $0 (INV-3).
  prompt: number | null
  completion: number | null
}

// Returns null when n_calls === 0 — renders nothing rather than an all-zero
// chart, matching the pre-split "no per-iter data yet" empty state.
export const deriveCostByCategory = (
  payload: CostChartsSnapshotPayload,
): CostByCategoryRow[] | null => {
  if (payload.spend.n_calls === 0) {
    return null
  }
  return [
    toRow("Architect", payload.architect_spend),
    toRow("Genetic", payload.genetic_spend),
    toRow("Evaluation", payload.evaluation_spend),
  ]
}

const toRow = (
  category: CostByCategoryRow["category"],
  spend: Spend,
): CostByCategoryRow => {
  const split = computeCostSplit({ spend })
  return {
    category,
    total: spend.total,
    prompt: split.promptCost,
    completion: split.completionCost,
  }
}
